import * as d3 from 'd3';
import { Line, Block } from '../../types';
import { STYLES } from '../../styles/constants';
import {
  calculateConnectionPoint,
  calculateElbowPoints,
  createArrowMarker,
  getAssociationLinePoints,
} from '../../utils/connectionUtils';

export class LineRenderer {
  private svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private diagramContainer: d3.Selection<HTMLDivElement, unknown, null, undefined>;
  private editMode: boolean;

  constructor(
    diagramContainer: d3.Selection<HTMLDivElement, unknown, null, undefined>,
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
    editMode: { enabled: boolean }
  ) {
    this.diagramContainer = diagramContainer;
    this.svg = svg;
    svg.style('z-index', '1');
    this.editMode = editMode.enabled;
    createArrowMarker(this.svg);
  }

  public renderLine(line: Line, fromBlock: Block, toBlock: Block, isSelected: boolean) {
    const geom = { container: this.diagramContainer };
    const isAssociation = line.type === 'association';
    const startPoint = calculateConnectionPoint(fromBlock, line?.fromSide as any, geom);
    const endPoint = calculateConnectionPoint(toBlock, line.toSide as any, geom);
    const points = isAssociation
      ? getAssociationLinePoints(fromBlock, toBlock, line, geom)
      : calculateElbowPoints(startPoint, endPoint);

    const linePath = d3.line<{ x: number; y: number }>()
      .x(d => d.x)
      .y(d => d.y)
      .curve(isAssociation ? d3.curveLinear : d3.curveMonotoneX);

    // Create a group for the line elements
    const lineGroup = this.svg.append('g');

    // Create invisible wider path for better hit detection
    const hitArea = lineGroup
      .append('path')
      .attr('d', linePath(points))
      .attr('fill', 'none')
      .attr('stroke', 'transparent')
      .attr('stroke-width', '15');

    // Visible path — associations are dotted links (BPMN), without arrowheads
    const visiblePath = lineGroup
      .append('path')
      .attr('d', linePath(points))
      .attr('fill', 'none')
      .attr('stroke', isSelected ? STYLES.colors.selected : STYLES.colors.arrow)
      .attr('stroke-width', isSelected ? 2 : 1.5)
      .attr('stroke-dasharray', isAssociation ? '4 4' : null)
      .attr('marker-end', isAssociation ? null : 'url(#arrowhead)')
      .style('pointer-events', 'none');

    return { hitArea, visiblePath, points };
  }
}